var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { call, select, put, cancelled, take, fork, cancel, actionChannel, delay, } from "@redux-saga/core/effects";
import { ConferenceSessionImpl } from "./ConferenceSessionImpl";
import { joinConferenceRequest, joinConferenceSuccess, joinConferenceFailure, leaveConferenceRequest, leaveConferenceSuccess, leaveConferenceFailure, endConferenceRequest, endConferenceSuccess, endConferenceFailure, getConferenceStateRequest, getConferenceStateSuccess, getConferenceStateFailure, conferenceEvent, conferenceHasEnded, conferenceHasFailed, } from "./actions";
import { identity as getIdentity, endpoint as getEndpoint } from "../selectors";
import { mqttEvent, killAllAction } from "../actions";
import { joinedConferenceSelector } from "./selectors";
import { ConferenceEventType } from "./model";
import { buffers } from "redux-saga";
import { mqttEventPattern, leaveConferenceRequestPattern, endConferenceRequestPattern, conferenceHasEndedPattern, killAllPattern, conferenceHasFailedPattern, } from "./patterns";
import { viewStreamTaskManager } from "./view/sagas";
import { publishStreamTaskManager } from "./publish/sagas";
import { log, eventLog } from "../../log";
export function* createConferenceSaga(args) {
    const { context, options, resolve, reject } = args;
    const conferenceResource = context.getConferenceResource();
    const body = {
        creatorEndpoint: context.endpoint(),
    };
    if (options === null || options === void 0 ? void 0 : options.metadata) {
        body.metadata = options.metadata;
    }
    if (options === null || options === void 0 ? void 0 : options.mode) {
        // @ts-expect-error
        body.mode = options.mode.toUpperCase();
    }
    if (options === null || options === void 0 ? void 0 : options.id) {
        body.conferenceId = options.id;
    }
    if (options === null || options === void 0 ? void 0 : options.interactionId) {
        body.interactionId = options.interactionId;
    }
    try {
        const response = yield conferenceResource.create(body, {
            urlPostfix: "create",
        });
        const conference = {
            name: response.id,
            mode: response.mode,
        };
        conference.mode = conference.mode.toLowerCase();
        context.sendEvent("conferenceCreated", conference);
        resolve(conference);
    }
    catch (error) {
        context.sendEvent("createConferenceFailed", error);
        reject(error);
    }
}
function* sendCleanUpEventsBeforeLeavingConference(context) {
    const userId = context.getParentContext().identity();
    log.debug(`sendCleanUpEventsBeforeLeavingConference for user ${userId}`);
    const userEndpointId = context.getParentContext().endpoint();
    const conferenceId = context.conference.name;
    const joinedConference = yield select(joinedConferenceSelector, conferenceId);
    for (const participant of joinedConference.conferenceState.participants) {
        for (const endpoint of participant.endpoints) {
            if (endpoint.id !== userEndpointId) {
                context.sendEvent("endpointLeft", {
                    username: participant.id,
                    endpoint: endpoint.id,
                });
            }
        }
    }
}
function* leaveConferenceSaga(context, reason, makeLeaveCall = true) {
    const conferenceResource = context.getConferenceResource();
    const identity = yield select(getIdentity);
    const endpoint = yield select(getEndpoint);
    const conferenceId = context.conference.name;
    try {
        yield put(leaveConferenceRequest({ name: conferenceId, reason }));
        yield call(sendCleanUpEventsBeforeLeavingConference, context);
        if (makeLeaveCall) {
            yield conferenceResource.create({
                conferenceId,
                userId: identity,
                userEndpointId: endpoint,
                reason: reason || "user requested to leave",
            }, { urlPostfix: "leave" });
        }
        yield put(leaveConferenceSuccess(conferenceId));
    }
    catch (error) {
        if (error instanceof Error) {
            yield put(leaveConferenceFailure({ conferenceId, message: error.message }));
        }
        else {
            yield put(leaveConferenceFailure({
                conferenceId,
                message: "unknown reason",
            }));
        }
        throw error;
    }
    finally {
        if (yield cancelled()) {
            yield put(leaveConferenceFailure({ conferenceId, message: "cancelled" }));
        }
    }
}
function* endConferenceSaga(context, reason) {
    const conferenceResource = context.getConferenceResource();
    const identity = yield select(getIdentity);
    const endpoint = yield select(getEndpoint);
    const conferenceId = context.conference.name;
    try {
        yield put(endConferenceRequest({ name: conferenceId, reason }));
        yield call(sendCleanUpEventsBeforeLeavingConference, context);
        yield conferenceResource.create({
            conferenceId,
            userId: identity,
            userEndpointId: endpoint,
            reason: reason || "user requested to end",
        }, { urlPostfix: "end" });
        yield put(endConferenceSuccess(conferenceId));
    }
    catch (error) {
        if (error instanceof Error) {
            yield put(endConferenceFailure({ conferenceId, message: error.message }));
        }
        else {
            yield put(endConferenceFailure({
                conferenceId,
                message: "unknown reason",
            }));
        }
        throw error;
    }
    finally {
        if (yield cancelled()) {
            yield put(endConferenceFailure({ conferenceId, message: "cancelled" }));
        }
    }
}
function* getConferenceStateSaga(context) {
    const conferenceResource = context.getConferenceResource();
    const conferenceId = context.conference.name;
    try {
        yield put(getConferenceStateRequest(conferenceId));
        const state = yield conferenceResource.get({
            urlPostfix: `${conferenceId}/simpleView`,
        });
        yield put(getConferenceStateSuccess(state));
    }
    catch (error) {
        if (error instanceof Error) {
            yield put(getConferenceStateFailure({
                conferenceId,
                message: error.message,
            }));
        }
        else {
            yield put(getConferenceStateFailure({
                conferenceId,
                message: "unknown reason",
            }));
        }
        throw error;
    }
    finally {
        if (yield cancelled()) {
            yield put(getConferenceStateFailure({
                conferenceId,
                message: "cancelled",
            }));
        }
    }
}
export function* postStatsNewSession(res, data) {
    try {
        yield res.create(data, { urlPostfix: "sessions" });
    }
    catch (error) {
        log.warn("postStatsNewSession failed", error);
    }
}
export function* updateConferenceMetadata(context, operation, key, value) {
    const conferenceResource = context.getConferenceResource();
    yield conferenceResource.create({
        conferenceId: context.conference.name,
        operation,
        userEndpointId: yield select(getEndpoint),
        userId: yield select(getIdentity),
        key,
        value: JSON.stringify(value),
    }, { urlPostfix: "updateMetadata" });
}
function* joinConferenceRequestSaga(context, options) {
    const endpoint = yield select(getEndpoint);
    const conferenceId = options.id;
    const metadata = options.metadata;
    const body = {
        conferenceId,
        userEndpointId: endpoint,
        metadata,
    };
    const conferenceResource = context.getConferenceResource();
    try {
        yield put(joinConferenceRequest(conferenceId));
        const response = yield conferenceResource.create(body, {
            urlPostfix: "join",
        });
        const { mode } = response;
        const { eventHandlers, viewStreamMetadataProvider } = options, optionsWithoutEventHandlers = __rest(options, ["eventHandlers", "viewStreamMetadataProvider"]);
        yield put(joinConferenceSuccess({
            conferenceId,
            mode,
            options: optionsWithoutEventHandlers,
        }));
    }
    catch (error) {
        yield put(joinConferenceFailure({ conferenceId, error }));
        throw error;
    }
    finally {
        if (yield cancelled()) {
            yield put(joinConferenceFailure({
                conferenceId,
                error: new Error("cancelled!"),
            }));
        }
    }
}
function* processingConferenceEventsSaga(context, conferenceEventChannel) {
    const conferenceId = context.conference.name;
    try {
        // process events one by one
        while (true) {
            const action = yield take(conferenceEventChannel);
            const event = action.payload;
            const currentState = yield select(joinedConferenceSelector, conferenceId);
            if (currentState == null) {
                throw new Error("WTF? currentState == null");
            }
            // the following if-then-else statement implements deduplication
            // and out-of-order event reception
            if (!Number.isInteger(event.conferenceVersion)) {
                if (event.type === ConferenceEventType.CONFERENCE_NETWORK_INDICATOR) {
                    yield call([context, context.processEvent], event);
                }
                else {
                    eventLog.debug("received event without conferenceVersion!", event);
                }
            }
            else if (event.conferenceVersion < currentState.conferenceState.version) {
                // do nothing we have processed this event previously
                log.debug("received an older event", event);
            }
            else if (event.conferenceVersion > currentState.conferenceState.version) {
                // append the event back to the queue
                const eventJson = JSON.stringify(event);
                // timeout if 5 seconds have elapsed from first attempt
                if (!!event.firstSeen && Date.now() - event.firstSeen > 5000) {
                    log.error(`event processing timeout: ${eventJson}`);
                    yield put(conferenceHasFailed(`event processing timeout: ${eventJson}`));
                }
                else if (!event.firstSeen) {
                    event.firstSeen = Date.now();
                }
                yield delay(100);
                log.debug(`resubmitting event for later processing: ${eventJson}`);
                yield put(mqttEvent(event));
            }
            else {
                // update store
                yield put(conferenceEvent(event));
                yield call([context, context.processEvent], action.payload);
            }
        }
    }
    catch (error) {
        log.warn("processingConferenceEventsSaga error", error);
    }
    finally {
        if (yield cancelled()) {
            log.debug("processingConferenceEventsSaga cancelled");
        }
        log.debug("processingConferenceEventsSaga stopped");
    }
}
export function* joinConferenceSaga(args) {
    var _a, _b;
    const { context, options, resolve, reject } = args;
    const conferenceSession = new ConferenceSessionImpl(context, options);
    const conferenceId = options.id;
    let resolved = false;
    let viewStreamManager = null;
    let publishStreamManager = null;
    let processingConferenceEventsTask = null;
    let conferenceEventChannel = null;
    try {
        conferenceSession.sendEvent("connecting");
        context.sendEvent("newConferenceSession", conferenceSession);
        // subscribe to conferenceId topic to exchange messages with peers
        const commonClient = context.getCommonClient();
        // join the conference
        yield call(joinConferenceRequestSaga, context, options);
        // create a channel for mqtt events for this conference
        conferenceEventChannel = yield actionChannel(mqttEventPattern(conferenceId), buffers.expanding());
        viewStreamManager = yield fork(viewStreamTaskManager, conferenceSession);
        publishStreamManager = yield fork(publishStreamTaskManager, conferenceSession);
        // give some time for above managers to listen
        yield delay(1);
        conferenceSession.sendEvent("accepted", conferenceSession);
        resolve(conferenceSession);
        resolved = true;
        // get the current conference state using simpleView
        yield call(getConferenceStateSaga, conferenceSession);
        // process the initial state and dispatch any events
        conferenceSession.processInitialState();
        // now start processing events received (and possibly buffered in conferenceEventChannel
        processingConferenceEventsTask = yield fork(processingConferenceEventsSaga, conferenceSession, conferenceEventChannel);
        // wait until the client leaves or ends the conference
        // or someone ends the conference
        const action = yield take([
            leaveConferenceRequestPattern(conferenceId),
            endConferenceRequestPattern(conferenceId),
            conferenceHasEndedPattern(conferenceId),
            conferenceHasFailedPattern(conferenceId),
            killAllPattern(),
        ]);
        if (action.type === leaveConferenceRequest.toString()) {
            yield call(leaveConferenceSaga, conferenceSession, action.payload.reason);
        }
        else if (action.type === endConferenceRequest.toString()) {
            yield call(endConferenceSaga, conferenceSession, action.payload.reason);
        }
        else if (action.type === conferenceHasEnded.toString()) {
            yield call(leaveConferenceSaga, conferenceSession, action.payload.reason, false);
        }
        else if (action.type === killAllAction.toString()) {
            throw new Error("Terminated by unhandled action");
        }
        else if (action.type === conferenceHasFailedPattern.toString()) {
            throw new Error(`Conference failed due to ${action.payload}`);
        }
        if ((_a = action === null || action === void 0 ? void 0 : action.payload) === null || _a === void 0 ? void 0 : _a.reason) {
            conferenceSession.sendEvent("ended", { reason: (_b = action === null || action === void 0 ? void 0 : action.payload) === null || _b === void 0 ? void 0 : _b.reason });
        }
        else {
            conferenceSession.sendEvent("ended");
        }
    }
    catch (error) {
        conferenceSession.sendEvent("failed", error);
        reject(error);
    }
    finally {
        // try to END channel
        if (conferenceEventChannel) {
            conferenceEventChannel.close();
        }
        const tasks = [
            processingConferenceEventsTask,
            viewStreamManager,
            publishStreamManager,
        ].filter((t) => !!t);
        if (tasks.length) {
            yield cancel(tasks);
        }
        if (!resolved && (yield cancelled())) {
            const error = new Error("cancelled!");
            conferenceSession.sendEvent("failed", error);
            reject(error);
        }
        if (yield cancelled()) {
            log.debug("joinConferenceSaga cancelled");
        }
        log.debug("joinConferenceSaga stopped");
    }
}
